import React, { useCallback, useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { PartyPopper, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fireBurst = () => {
  const duration = 2500;
  const end = Date.now() + duration;
  const colors = ['#10b981', '#f59e0b', '#38bdf8', '#a855f7', '#ffffff'];
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 60,
      origin: { x: 0, y: 0.75 },
      colors,
      startVelocity: 45,
      scalar: 1.05,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 60,
      origin: { x: 1, y: 0.75 },
      colors,
      startVelocity: 45,
      scalar: 1.05,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

export default function MRRMilestoneBanner() {
  const [milestone, setMilestone] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const firedRef = useRef(null); // milestone_id we already threw confetti for

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/milestones/latest`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setMilestone(data.milestone || null);
    } catch (e) {
      console.error('milestone load failed', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fire confetti exactly once per milestone_id (per session/mount)
  useEffect(() => {
    if (!milestone) return;
    if (firedRef.current === milestone.milestone_id) return;
    firedRef.current = milestone.milestone_id;
    fireBurst();
    toast.success(
      `🎉 MRR crossed $${Number(milestone.tier).toLocaleString()} — huge win!`,
      { duration: 6000 }
    );
  }, [milestone]);

  const handleDismiss = async () => {
    if (!milestone) return;
    setDismissing(true);
    try {
      await fetch(
        `${API_URL}/api/owner-admin/milestones/${encodeURIComponent(milestone.milestone_id)}/dismiss`,
        { method: 'POST', credentials: 'include' },
      );
      setMilestone(null);
    } catch (e) {
      console.error(e);
    } finally {
      setDismissing(false);
    }
  };

  const handleCelebrateAgain = () => {
    fireBurst();
  };

  if (!milestone) return null;

  const tier = Number(milestone.tier || 0);
  const next = Number(milestone.next_target || tier * 5);
  const currentMrr = Number(milestone.mrr_at_hit || 0);
  const toGo = Math.max(0, next - currentMrr);

  return (
    <Card
      className="border-emerald-400/40 bg-gradient-to-r from-emerald-500/10 via-emerald-400/5 to-amber-400/10 shadow-[0_0_60px_-20px_rgba(16,185,129,0.35)]"
      data-testid="mrr-milestone-banner"
    >
      <CardContent className="py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-emerald-500/20 border border-emerald-400/40">
              <PartyPopper className="h-6 w-6 text-emerald-300" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-300/80 font-semibold">
                Milestone unlocked
              </p>
              <p className="text-2xl font-bold text-white leading-tight">
                MRR just crossed ${tier.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Current MRR ${currentMrr.toLocaleString(undefined, { minimumFractionDigits: 2 })} ·
                {' '}${toGo.toLocaleString(undefined, { maximumFractionDigits: 0 })} to your next target (${next.toLocaleString()})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCelebrateAgain}
              className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/20"
              data-testid="milestone-celebrate-again"
            >
              <PartyPopper className="h-4 w-4 mr-1" /> Celebrate again
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              disabled={dismissing}
              className="text-muted-foreground hover:text-white"
              data-testid="milestone-dismiss"
            >
              <X className="h-4 w-4 mr-1" /> Dismiss
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
